import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import Modal from '../../../common/modal/Modal';

import { getPrivateService, postContractDeposits } from '../../../../actions';
import PrivateServiceDepositForm from './PrivateServiceDepositForm';
import { tzNormalizeDate } from '../../../../utils/date';

export const PrivateServiceDepositButton = ({
  contract,
  dispatchPostContractDeposits,
  dispatchGetPrivateService,
}) => {
  const [showModal, setShowModal] = useState(false);

  const [currentContract, setCurrentContract] = useState(contract);

  useEffect(() => {
    if (!contract) return;

    setCurrentContract((current) => {
      if (current && current.id === contract.id) {
        return current;
      }

      return contract;
    });
  }, [contract]);

  const parseAmount = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalizedValue = String(value).trim().replace(',', '.');

    const numericValue = Number(normalizedValue);

    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const contractDepositList = Array.isArray(currentContract.contractDepositList)
    ? currentContract.contractDepositList
    : [];

  const totalDeposited = contractDepositList.reduce((total, deposit) => {
    if (!deposit) {
      return total;
    }

    return total + parseAmount(deposit.actualAmountDeposited);
  }, 0);

  const totalAmount = parseAmount(currentContract.totalAmount);

  const remainingAmount = Math.max(totalAmount - totalDeposited, 0);

  const contractFullyPaid = totalAmount > 0 && remainingAmount <= 0;

  const getContractFromResponse = (response) => {
    if (!response) {
      return null;
    }

    if (response.id) {
      return response;
    }

    if (response.data && response.data.id) {
      return response.data;
    }

    if (response.payload && response.payload.id) {
      return response.payload;
    }

    return null;
  };

  const onModalSubmit = async ({ contractDepositList: formDeposits }) => {
    if (!Array.isArray(formDeposits) || formDeposits.length === 0) {
      toastr.error('Error', 'Debe ingresar al menos un depósito');
      return;
    }

    const depositsToSave = formDeposits.map((deposit) => ({
      bankAccountId: deposit.bankAccountId,

      agencyId:
        deposit.agencyId !== null &&
        deposit.agencyId !== undefined &&
        deposit.agencyId !== ''
          ? Number(deposit.agencyId)
          : null,

      operationNumber: deposit.operationNumber,

      actualAmountDeposited: parseAmount(deposit.actualAmountDeposited),

      depositDate: tzNormalizeDate({
        date: deposit.depositDate,
      }),

      depositDifferenceAmount:
        deposit.depositDifferenceAmount !== null &&
        deposit.depositDifferenceAmount !== undefined &&
        deposit.depositDifferenceAmount !== ''
          ? parseAmount(deposit.depositDifferenceAmount)
          : null,

      depositDifferenceReasonId:
        deposit.depositDifferenceReasonId !== null &&
        deposit.depositDifferenceReasonId !== undefined &&
        deposit.depositDifferenceReasonId !== ''
          ? Number(deposit.depositDifferenceReasonId)
          : null,
    }));

    const newDepositTotal = depositsToSave.reduce(
      (total, deposit) => total + parseAmount(deposit.actualAmountDeposited),
      0,
    );

    if (newDepositTotal <= 0) {
      toastr.error('Error', 'El monto del depósito debe ser mayor a cero');
      return;
    }

    if (newDepositTotal > remainingAmount) {
      toastr.error('Error', 'El monto ingresado supera el saldo pendiente');
      return;
    }

    const result = await dispatchPostContractDeposits({
      contractId: currentContract.id,
      contractDepositList: depositsToSave,
    });

    if (!result) {
      return;
    }

    const localUpdatedDepositList = [...contractDepositList, ...depositsToSave];

    setCurrentContract((previousContract) => ({
      ...previousContract,
      contractDepositList: localUpdatedDepositList,
    }));

    const response = await dispatchGetPrivateService({
      privateServiceId: currentContract.id,
    });

    const updatedContract = getContractFromResponse(response);

    if (updatedContract) {
      const backendDepositList = Array.isArray(
        updatedContract.contractDepositList,
      )
        ? updatedContract.contractDepositList
        : null;

      setCurrentContract((previousContract) => ({
        ...previousContract,
        ...updatedContract,
        contractDepositList:
          backendDepositList !== null
            ? backendDepositList
            : localUpdatedDepositList,
      }));
    }

    toastr.success('Éxito', 'Depósito registrado correctamente');

    setShowModal(false);
  };
  const onCancel = () => {
    setShowModal(false);
  };

  const onShowModal = () => {
    setShowModal(true);
  };

  return (
    <>
      <Button
        color="success"
        onClick={onShowModal}
        disabled={contractFullyPaid}
      >
        <i className="fa fa-plus-circle" /> Registrar depósito
      </Button>

      <Modal
        show={showModal}
        title="Registrar Depósito de Contrato"
        onClickClose={onCancel}
        onClickCancel={onCancel}
        body={
          <PrivateServiceDepositForm
            totalAmount={totalAmount}
            totalDeposited={totalDeposited}
            remainingAmount={remainingAmount}
            handleProcess={onModalSubmit}
          />
        }
        size="xl"
      />
    </>
  );
};

PrivateServiceDepositButton.propTypes = {
  contract: PropTypes.shape({
    id: PropTypes.number.isRequired,

    totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    contractDepositList: PropTypes.arrayOf(
      PropTypes.shape({
        bankAccountId: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),

        operationNumber: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),

        actualAmountDeposited: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),

        depositDate: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
          PropTypes.object,
        ]),

        depositDifferenceAmount: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),

        depositDifferenceReasonId: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
      }),
    ),
  }).isRequired,

  dispatchPostContractDeposits: PropTypes.func.isRequired,

  dispatchGetPrivateService: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  dispatchPostContractDeposits: postContractDeposits,

  dispatchGetPrivateService: getPrivateService,
};

export default connect(null, mapDispatchToProps)(PrivateServiceDepositButton);
